import type { Translation } from '../../types';
import type { commerce as en } from '../en/commerce';

export const commerce: Translation<typeof en> = {
  crumb: 'Kinh doanh',
  filter: { customer: 'Khách hàng: {{name}}', customerUnknown: 'Chỉ một khách hàng' },
  pager: { newer: 'Mới hơn', older: 'Cũ hơn', page: 'Trang {{n}}' },
  common: {
    status: 'Trạng thái',
    customer: 'Khách hàng',
    order: 'Đơn hàng',
    domain: 'Tên miền',
    product: 'Sản phẩm',
    plan: 'Gói dịch vụ',
    operation: 'Nghiệp vụ',
    amount: 'Số tiền',
    subtotal: 'Tạm tính',
    tax: 'Thuế',
    total: 'Tổng cộng',
    created: 'Ngày tạo',
    updated: 'Cập nhật',
    issued: 'Ngày phát hành',
    posted: 'Ghi sổ',
    currency: 'Tiền tệ',
    purpose: 'Mục đích',
    provider: 'Nhà cung cấp',
    none: 'Không có',
    yes: 'Có',
    no: 'Không',
    platform: 'Nền tảng',
    years: '{{count}} năm',
    yearsLong_one: '{{count}} năm',
    yearsLong_other: '{{count}} năm',
    premium: 'Premium',
    expires: 'hết hạn',
    reversed: 'đã đảo',
    debitShort: 'Nợ',
    creditShort: 'Có',
    debit: 'Nợ',
    credit: 'Có',
    entry: 'Bút toán',
    type: 'Loại',
    description: 'Diễn giải',
    resource: 'Cho',
    postings: 'Dòng hạch toán',
    account: 'Tài khoản',
    noJournal: 'Chưa có bút toán',
    rawAmount: 'Số tiền chính xác: {{value}}',
    noReference: 'Chưa có mã tham chiếu',
    copyId: 'Mã định danh',
  },
  quotes: {
    title: 'Báo giá',
    subtitle: 'Giá đã báo cho khách hàng của từng tên miền hoặc dịch vụ và mức giá được chốt tại thời điểm đó.',
    domainFilter: 'Tên miền',
    domainPlaceholder: 'Tiền tố tên miền',
    loadError: 'Không tải được danh sách báo giá',
    empty: 'Không có báo giá phù hợp',
    col: { domain: 'Sản phẩm', operation: 'Nghiệp vụ', customer: 'Khách hàng', subtotal: 'Tạm tính', tax: 'Thuế', total: 'Tổng cộng', state: 'Tình trạng', created: 'Ngày tạo' },
    state: { ORDERED: 'Đã đặt hàng', EXPIRED: 'Hết hạn', OPEN: 'Còn hiệu lực' },
  },
  quote: {
    title: 'Báo giá',
    titleFor: 'Báo giá cho {{domain}}',
    loadError: 'Không tải được báo giá',
    field: { id: 'Mã báo giá', state: 'Tình trạng', tld: 'TLD', premium: 'Premium', expires: 'Hiệu lực đến' },
    breakdown: 'Giá này được tính thế nào',
    bd: {
      registryCost: 'Giá vốn registry',
      converted: 'Quy đổi sang tiền thanh toán',
      rate: '1 {{base}} = {{rate}}',
      priceBook: 'Bảng giá bán',
      rule: 'Quy tắc giá',
      base: 'Giá trước khuyến mãi',
      promotion: 'Khuyến mãi',
      tax: 'Thuế {{code}} ({{rate}} %)',
      version: 'phiên bản {{n}}',
      premiumCost: 'Giá vốn tên miền premium',
      noPromotion: 'Không áp dụng',
    },
    immutable: 'Báo giá không bao giờ thay đổi. Nó lưu lại mọi thành phần giá cùng phiên bản bảng giá, tỷ giá và thuế suất đang hiệu lực, nên số tiền đã thu luôn giải thích được.',
  },
  orders: {
    title: 'Đơn hàng',
    subtitle: 'Khách hàng đã đặt những tên miền hoặc dịch vụ nào, giá trị bao nhiêu và đã xử lý đến đâu.',
    loadError: 'Không tải được danh sách đơn hàng',
    empty: 'Không có đơn hàng phù hợp',
    col: { order: 'Sản phẩm đã đặt', customer: 'Khách hàng', status: 'Trạng thái', items: 'Số sản phẩm', total: 'Tổng cộng', placed: 'Ngày đặt', updated: 'Cập nhật' },
  },
  order: {
    title: 'Đơn hàng',
    titleFor: 'Đơn hàng cho {{domain}}',
    loadError: 'Không tải được đơn hàng',
    field: { placed: 'Ngày đặt', id: 'Mã đơn hàng' },
    stat: { status: 'Trạng thái đơn hàng', total: 'Tổng giá trị đơn', items: 'Số sản phẩm', fulfilled: '{{count}} đã hoàn tất' },
    tab: { items: 'Sản phẩm ({{count}})', ledger: 'Bút toán ({{count}})', documents: 'Thanh toán, hoá đơn và hoàn tiền ({{count}})' },
    itemCol: { domain: 'Sản phẩm', operation: 'Nghiệp vụ', status: 'Trạng thái', amount: 'Số tiền', hold: 'Tiền tạm giữ', quote: 'Báo giá', workflow: 'Quy trình' },
    viewQuote: 'Xem báo giá',
    viewWorkflow: 'Tiến trình xử lý',
    ledgerEmpty: 'Chưa ghi sổ: tiền chỉ được tạm giữ cho đến khi sản phẩm được bàn giao.',
    noDocuments: 'Không có. Đơn hàng được thanh toán từ ví khách hàng; hoá đơn phát hành sau khi hoàn tất.',
    payments: 'Thanh toán',
    invoices: 'Hoá đơn và phiếu điều chỉnh',
    refunds: 'Hoàn tiền',
    issuedAt: 'phát hành {{at}}',
  },
  billing: {
    title: 'Tổng quan thanh toán',
    subtitle: 'Số dư của khách hàng, phần tiền đang tạm giữ, cùng các khoản đã thu, đã hoàn và đã xuất hoá đơn.',
    limitTitle: 'Chưa kết nối cổng thanh toán hay nhà cung cấp hoá đơn điện tử',
    limit: 'Thanh toán chỉ chạy qua nhà cung cấp thử nghiệm nội bộ và hoá đơn vẫn là chứng từ nội bộ: chưa có giao dịch nào làm dịch chuyển tiền thật hay được gửi tới cơ quan thuế.',
    loadError: 'Không tải được tổng quan thanh toán',
    wallets: 'Ví khách hàng ({{currency}})',
    walletCount_one: '{{count}} ví',
    walletCount_other: '{{count}} ví',
    availableLabel: 'Số dư khả dụng',
    heldLabel: 'Tạm giữ',
    walletLink: 'Tài khoản ví',
    noWallets: 'Chưa có tài khoản ví khách hàng.',
    openHolds: 'Khoản tạm giữ đang mở',
    openHoldsHint: 'Tiền đang giữ cho các đơn hàng chưa hoàn tất',
    noOpenHolds: 'Không có khoản tạm giữ nào',
    failedPayments: 'Thanh toán thất bại',
    unknownPayments: '{{count}} giao dịch chờ đối soát',
    noFailedPayments: 'Không có thanh toán thất bại',
    imbalance: 'Chênh lệch sổ cái',
    balanced: 'Cân đối',
    imbalanced: 'Mất cân đối',
    imbalanceHint: 'Số bút toán không cân (phải bằng 0)',
    ledgerLink: 'Ví / Sổ cái',
    payments: 'Thanh toán',
    holds: 'Tạm giữ số dư',
    refunds: 'Hoàn tiền',
    invoices: 'Hoá đơn và phiếu điều chỉnh',
    noneRecorded: 'Chưa ghi nhận.',
    noneIssued: 'Chưa phát hành.',
    col: { status: 'Trạng thái', count: 'Số lượng', amount: 'Số tiền', document: 'Chứng từ' },
    holdsList: 'Danh sách tạm giữ',
    holdsLoadError: 'Không tải được danh sách tạm giữ',
    holdsEmpty: 'Không có khoản tạm giữ phù hợp',
    holdCol: { hold: 'Giữ cho', account: 'Tài khoản', purpose: 'Mục đích', status: 'Trạng thái', amount: 'Số tiền', created: 'Ngày tạo', closes: 'Hết hạn / đóng' },
    latestEntries: 'Bút toán mới nhất',
    latestPayments: 'Thanh toán mới nhất',
    noPayments: 'Chưa có thanh toán.',
    viewAll: 'Xem tất cả',
  },
  hold: {
    title: 'Khoản tạm giữ',
    titleFor: 'Tiền tạm giữ cho {{domain}}',
    loadError: 'Không tải được khoản tạm giữ',
    explain: 'Khoản tạm giữ đang mở giữ riêng một phần tiền trong ví khách hàng: tiền vẫn của khách nhưng không thể dùng hai lần. Tiền được quyết toán khi tên miền đăng ký thành công, được giải phóng khi thất bại và tự hết hạn nếu không có gì diễn ra.',
    field: { id: 'Mã khoản tạm giữ', account: 'Tài khoản ví', capture: 'Bút toán quyết toán', expires: 'Hết hạn', closed: 'Đã đóng', reserved: 'Giữ cho' },
    stat: { status: 'Trạng thái tạm giữ', amount: 'Số tiền tạm giữ', available: 'Khả dụng sau khoản này' },
    item: 'Tên miền mà khoản tiền này dành cho',
    itemField: { status: 'Trạng thái xử lý', quote: 'Báo giá', amount: 'Giá' },
    noItem: 'Khoản tiền này không gắn với dòng đơn hàng nào.',
    entries: 'Bút toán của khoản tạm giữ này',
    noEntries: 'Chưa ghi sổ: tiền chỉ dịch chuyển khi khoản tạm giữ được quyết toán.',
    noAccount: 'Không còn truy cập được tài khoản ví.',
  },
  ledger: {
    title: 'Ví / Sổ cái',
    subtitle: 'Mọi dịch chuyển tiền, ghi kép. Điều chỉnh được ghi bằng bút toán đảo, không bao giờ sửa trực tiếp.',
    view: 'Chế độ xem sổ cái',
    tab: { accounts: 'Tài khoản', journal: 'Nhật ký' },
    trialBalance: 'Bảng cân đối thử',
    trialLoadError: 'Không tải được bảng cân đối thử',
    balancedToZero: 'Cân đối về 0',
    imbalancedCount_one: 'Mất cân đối: {{count}} bút toán',
    imbalancedCount_other: 'Mất cân đối: {{count}} bút toán',
    trialHint: 'Mỗi bút toán được kiểm tra khi ghi sổ — Nợ bằng Có theo từng loại tiền — và các dòng hạch toán sau đó không thể sửa.',
    entriesPosted: 'Bút toán đã ghi sổ',
    imbalancedEntries: 'Bút toán không cân',
    mustBeZero: 'Phải bằng 0',
    currencies: 'Loại tiền',
    noAccounts: 'Chưa có tài khoản sổ cái.',
    trialCol: { currency: 'Tiền tệ', accounts: 'Tài khoản', postings: 'Dòng hạch toán', debits: 'Tổng Nợ', credits: 'Tổng Có', difference: 'Nợ − Có' },
    codeFilter: 'Mã',
    codePlaceholder: 'Tiền tố, vd. customer:',
    owner: 'Chủ sở hữu',
    type: 'Loại',
    currency: 'Tiền tệ',
    accountsLoadError: 'Không tải được tài khoản sổ cái',
    accountsEmpty: 'Không có tài khoản phù hợp',
    accountCol: { account: 'Tài khoản', type: 'Loại', owner: 'Chủ sở hữu', debits: 'Nợ', credits: 'Có', balance: 'Số dư', held: 'Tạm giữ', available: 'Khả dụng', status: 'Trạng thái' },
    balanceNote: 'Số dư tính theo phía thông thường của tài khoản: Nợ − Có với tài sản và chi phí, Có − Nợ với các loại còn lại.',
    entryType: 'Loại bút toán',
    resource: 'Cho',
    journalLoadError: 'Không tải được nhật ký bút toán',
    journalEmpty: 'Không có bút toán phù hợp',
  },
  ledgerAccount: {
    title: 'Tài khoản sổ cái',
    crumb: 'Tài khoản',
    loadError: 'Không tải được tài khoản',
    field: { code: 'Mã tài khoản', negative: 'Số dư âm', allowed: 'Cho phép', rejected: 'Không cho phép', opened: 'Mở ngày', debits: 'Tổng Nợ', credits: 'Tổng Có', postings: 'Số dòng hạch toán' },
    stat: { balance: 'Số dư', held: 'Tạm giữ', available: 'Số dư khả dụng' },
    activeHolds: 'Khoản tạm giữ đang mở',
    postings: 'Dòng hạch toán (200 dòng mới nhất)',
    noPostings: 'Chưa có dòng hạch toán.',
    allEntries: 'Tất cả bút toán',
  },
  journal: {
    title: 'Bút toán',
    loadError: 'Không tải được bút toán',
    field: { id: 'Mã bút toán', entryType: 'Loại bút toán', effective: 'Hiệu lực', actor: 'Người ghi sổ', correlation: 'Mã tương quan', reverses: 'Điều chỉnh cho', reversedBy: 'Được điều chỉnh bởi', approval: 'Phê duyệt', request: 'Mã yêu cầu' },
    balanced: 'Nợ = Có',
    unbalanced: 'Không cân',
    total: 'Tổng {{currency}}',
    immutable: 'Bút toán không bao giờ được sửa. Điều chỉnh được ghi bằng bút toán đảo trỏ về bút toán gốc.',
  },
  payments: {
    title: 'Thanh toán',
    subtitle: 'Yêu cầu thanh toán bằng chuyển khoản mã QR (nạp ví và thanh toán đơn hàng), tiền đã về hay chưa, và các khoản hoàn tiền.',
    purposeFilter: 'Mục đích',
    loadError: 'Không tải được danh sách yêu cầu thanh toán',
    empty: 'Không có yêu cầu thanh toán phù hợp',
    col: {
      payment: 'Mã tham chiếu',
      code: 'Nội dung chuyển khoản',
      customer: 'Khách hàng',
      purpose: 'Mục đích',
      status: 'Trạng thái',
      amount: 'Số tiền',
      settled: 'Đã cộng ví',
      expires: 'Hết hạn',
      created: 'Ngày tạo',
    },
    bankTransactions: 'Giao dịch ngân hàng',
    attention: 'Phiên cần xử lý',
    attentionHint: 'Yêu cầu có phiên cổng thanh toán chưa rõ kết quả, quá hạn mà chưa kết thúc, có tiền không khớp phiên, hoặc có khoản hoàn về nguồn chờ cổng quá 1 ngày',
    refunds: 'Hoàn tiền',
    refundsLoadError: 'Không tải được danh sách hoàn tiền',
    refundsEmpty: 'Không có khoản hoàn tiền phù hợp',
    refundCol: { refund: 'Tên miền hoàn tiền', customer: 'Khách hàng', order: 'Đơn hàng', reason: 'Lý do', status: 'Trạng thái', amount: 'Số tiền', entry: 'Bút toán', created: 'Ngày tạo' },
    refundNoName: 'Khoản hoàn tiền',
  },
  duplicateRef: {
    found_one: 'Mã giao dịch này đã được ghi nhận {{count}} lần. Kiểm tra để không cộng tiền hai lần:',
    found_other: 'Mã giao dịch này đã được ghi nhận {{count}} lần. Kiểm tra để không cộng tiền hai lần:',
    kind: { BANK_TRANSACTION: 'Giao dịch ngân hàng', WALLET_ADJUSTMENT: 'Điều chỉnh số dư' },
    acknowledge: 'Tôi đã kiểm tra: đây là một khoản tiền khác, vẫn ghi nhận.',
  },
  payment: {
    title: 'Yêu cầu thanh toán',
    titleFor: 'Thanh toán {{code}}',
    loadError: 'Không tải được yêu cầu thanh toán',
    notRequest: 'Khoản thanh toán này không tạo bằng mã QR chuyển khoản (loại thanh toán trước đây): chỉ hiển thị bản ghi.',
    field: {
      id: 'Mã định danh',
      code: 'Nội dung chuyển khoản',
      reference: 'Mã tham chiếu',
      method: 'Tài khoản nhận tiền',
      provider: 'Phương thức',
      expires: 'Hết hạn lúc',
      settled: 'Đã cộng vào ví',
      settlement: 'Bút toán quyết toán',
      orderStatus: 'Trạng thái đơn hàng',
      bankTransaction: 'Giao dịch ngân hàng',
      payLink: 'Liên kết thanh toán',
    },
    qr: {
      title: 'Mã QR gửi khách hàng',
      hint: 'Khách hàng quét mã bằng ứng dụng ngân hàng: số tài khoản, số tiền và nội dung đã điền sẵn. Nội dung chuyển khoản phải giữ đúng {{code}}.',
      closed: 'Không còn mã để hiển thị: yêu cầu không còn mở.',
      payload: 'Chuỗi dữ liệu QR (EMVCo)',
    },
    tab: { transactions: 'Giao dịch ngân hàng ({{count}})', sessions: 'Phiên cổng ({{count}})', approvals: 'Phê duyệt ({{count}})', refunds: 'Hoàn tiền ({{count}})', sourceRefunds: 'Hoàn tiền về nguồn ({{count}})' },
    noTransactions: 'Chưa có giao dịch ngân hàng nào ghi mã của yêu cầu này.',
    noSessions: 'Khách chưa mở cổng thanh toán nào từ liên kết của yêu cầu này.',
    sessionsHint: 'Mỗi lần khách mở một cổng thanh toán từ liên kết là một phiên. Tiền cổng báo về luôn vào ví khách, kể cả khi phiên đã hết hạn hoặc bị huỷ.',
    sessionCol: { status: 'Trạng thái', gateway: 'Cổng · hình thức', providerRef: 'Mã giao dịch của cổng', providerCode: 'Mã kết quả', created: 'Bắt đầu lúc', expires: 'Hết hạn lúc', money: 'Tiền về' },
    session: {
      sync: 'Tra trạng thái',
      onGatewayPage: 'Khách chọn trên trang cổng',
      checkedAt: 'Tra lần cuối {{at}}',
      notCaptured: 'Tiền đã về nhưng phiên chưa ghi nhận đã thu: kiểm tra giao dịch.',
      amountDiffers: 'Cổng báo {{amount}}, khác số tiền của phiên.',
    },
    refund: {
      action: 'Hoàn tiền về nguồn',
      title: 'Hoàn tiền về thẻ/tài khoản khách đã trả',
      description: 'Trừ số tiền khỏi ví khách và yêu cầu cổng thanh toán trả về đúng thẻ hoặc tài khoản đã thanh toán. Nếu cổng không hoàn được, tiền tự trả lại ví.',
      amount: 'Số tiền hoàn (VND)',
      amountHint: 'Số đồng chẵn, tối đa {{max}} (số tiền cổng đã thu trừ phần đã hoàn).',
      amountInvalid: 'Nhập số đồng chẵn lớn hơn 0.',
      threshold: 'Từ {{threshold}} trở lên cần người thứ hai phê duyệt trước.',
      submit: 'Hoàn tiền',
      done: 'Đã trừ ví và gửi yêu cầu hoàn tiền cho cổng. Trạng thái cập nhật khi cổng xử lý xong.',
      requested: 'Đã gửi đề nghị phê duyệt. Khi người khác phê duyệt, hãy hoàn tiền lại với đúng số tiền này.',
      insufficient: 'Ví khách không đủ tiền: số dư khả dụng {{available}}.',
    },
    resolve: {
      action: 'Ghi nhận kết quả',
      title: 'Ghi nhận kết quả hoàn tiền trên cổng',
      description: 'Cổng chưa báo kết quả khoản hoàn này. Kiểm tra trên trang quản trị của cổng rồi ghi nhận: cổng đã hoàn về thẻ hoặc tài khoản khách, hoặc cổng không hoàn (tiền trả lại ví khách).',
      status: 'Kết quả trên cổng',
      completed: 'Cổng đã hoàn tiền cho khách',
      failed: 'Cổng không hoàn tiền, trả lại ví khách',
      threshold: 'Trả lại ví từ {{threshold}} trở lên cần người thứ hai phê duyệt trước.',
      submit: 'Ghi nhận',
      done: 'Đã ghi nhận kết quả hoàn tiền.',
      requested: 'Đã gửi đề nghị phê duyệt. Khi người khác phê duyệt, hãy ghi nhận lại kết quả này.',
    },
    refundsHint: 'Khoản hoàn về thẻ hoặc tài khoản khách đã trả qua cổng. Tiền đã trừ khỏi ví; khoản không thành công được trả lại ví.',
    noSourceRefunds: 'Chưa hoàn tiền về nguồn cho yêu cầu này.',
    refundCol: { status: 'Trạng thái', amount: 'Số tiền', reason: 'Lý do', providerRef: 'Mã hoàn của cổng', providerCode: 'Mã kết quả', entry: 'Bút toán', created: 'Tạo lúc', checked: 'Tra lần cuối' },
    noApprovals: 'Chưa xin phê duyệt.',
    approvalCol: { status: 'Trạng thái', amount: 'Số tiền', requestedBy: 'Người đề nghị', decidedBy: 'Người quyết định', requestedAt: 'Đề nghị lúc' },
    noRefunds: 'Không có khoản hoàn tiền cho giao dịch này.',
    confirm: {
      action: 'Xác nhận đã nhận tiền',
      title: 'Xác nhận đã nhận chuyển khoản',
      description: 'Bạn đã thấy giao dịch chuyển khoản của yêu cầu này trên sao kê ngân hàng mà chưa nguồn giao dịch nào báo về. Số tiền được cộng vào ví khách hàng.',
      amount: 'Số tiền đã nhận (VND)',
      amountHint: 'Số đồng chẵn, đúng như trên sao kê.',
      amountInvalid: 'Nhập số đồng chẵn lớn hơn 0.',
      reference: 'Mã giao dịch ngân hàng',
      referenceHint: 'Bắt buộc. Mã tham chiếu của dòng sao kê, ví dụ FT26270001.',
      threshold: 'Từ {{threshold}} trở lên cần người thứ hai phê duyệt trước.',
      submit: 'Xác nhận',
      done: 'Đã ghi nhận giao dịch và cộng tiền vào ví.',
      requested: 'Đã gửi đề nghị phê duyệt. Khi người khác phê duyệt (mục Phê duyệt), hãy xác nhận lại với đúng số tiền này.',
    },
    cancel: {
      action: 'Huỷ yêu cầu',
      title: 'Huỷ yêu cầu thanh toán',
      description: 'Mã QR hết hiệu lực. Đơn hàng đang chờ khoản thanh toán này cũng bị huỷ.',
      submit: 'Huỷ yêu cầu',
      done: 'Đã huỷ yêu cầu thanh toán.',
      orderCancelled: 'Đã huỷ yêu cầu thanh toán và đơn hàng đang chờ nó.',
    },
  },
  bankTxs: {
    title: 'Giao dịch ngân hàng',
    subtitle: 'Mọi giao dịch trên tài khoản nhận tiền do nguồn giao dịch (TinoPay, SePay, Casso) báo về và kết quả xử lý: đã khớp yêu cầu thanh toán, chờ nhân viên xem xét hay đã bỏ qua.',
    feedsTitle: 'Nguồn giao dịch ngân hàng',
    feedOn: 'Đang bật',
    feedOff: 'Đang tắt',
    feedLast: 'Giao dịch gần nhất {{when}}',
    feedNever: 'Chưa có giao dịch',
    webhook: 'Địa chỉ webhook',
    noFeeds: 'Chưa thiết lập nguồn giao dịch: chuyển khoản sẽ không được tự nhận diện. Thiết lập SePay, Casso hoặc TinoPay ở Cài đặt › Module › Thanh toán.',
    qr: {
      OK: 'Tài khoản hiện trên mã QR: {{connection}}',
      settingsLink: 'Cài đặt › Module › Thanh toán',
      NO_ACCOUNT: {
        title: 'Chưa bật tài khoản nhận tiền',
        body: 'Khách hàng chưa thanh toán bằng chuyển khoản được cho tới khi một kết nối đang bật có tài khoản nhận tiền: chọn “Ngân hàng của tài khoản” và nhập “Tên chủ tài khoản” cho kết nối đó ở',
      },
      AMBIGUOUS: {
        title: 'Chưa chọn tài khoản hiện trên mã QR',
        body: 'Nhiều kết nối đang bật có tài khoản nhận tiền ({{connections}}), nên chưa tạo được mã QR chuyển khoản. Chọn một kết nối ở mục “Kết nối hiện trên mã QR chuyển khoản” trong Cài đặt chung của mảng,',
      },
      NOT_USABLE: {
        title: 'Kết nối hiện trên mã QR không dùng được',
        body: 'Kết nối {{connections}} được chọn ở mục “Kết nối hiện trên mã QR chuyển khoản” đang tắt, không còn, gõ sai tên hoặc chưa có tài khoản nhận tiền, nên chưa tạo được mã QR chuyển khoản. Bật kết nối, nhập tài khoản nhận tiền hoặc chọn kết nối khác ở',
      },
    },
    stat: { unmatched: 'Chưa khớp', review: 'Chờ xem xét', hint: 'Gán cho yêu cầu, cộng vào ví hoặc bỏ qua từng giao dịch.' },
    statusFilter: 'Trạng thái',
    sourceFilter: 'Nguồn',
    search: 'Tìm',
    searchPlaceholder: 'Nội dung, mã, mã giao dịch, số tiền',
    open: 'Cần xử lý (chưa khớp hoặc chờ xem xét)',
    loadError: 'Không tải được giao dịch ngân hàng',
    empty: 'Không có giao dịch phù hợp',
    col: { received: 'Nhận lúc', source: 'Nguồn', amount: 'Số tiền', content: 'Nội dung chuyển khoản', status: 'Trạng thái', request: 'Yêu cầu thanh toán', customer: 'Khách hàng' },
  },
  bankTx: {
    title: 'Giao dịch ngân hàng',
    titleFor: 'Giao dịch {{reference}}',
    loadError: 'Không tải được giao dịch ngân hàng',
    field: {
      id: 'Mã định danh',
      source: 'Nguồn',
      externalId: 'Mã giao dịch ở nguồn',
      direction: 'Chiều',
      account: 'Tài khoản',
      content: 'Nội dung chuyển khoản',
      code: 'Mã tìm thấy',
      reference: 'Mã giao dịch ngân hàng',
      counterparty: 'Người chuyển',
      booked: 'Ngân hàng ghi nhận',
      received: 'Nhận lúc',
      request: 'Yêu cầu thanh toán',
      resolvedBy: 'Người xử lý',
      resolvedAt: 'Xử lý lúc',
      note: 'Ghi chú',
    },
    payload: 'Dữ liệu nguồn gửi về',
    suggestions: 'Yêu cầu đang mở có cùng số tiền',
    noSuggestions: 'Không có yêu cầu đang mở nào có số tiền này.',
    useSuggestion: 'Gán cho yêu cầu này',
    assign: {
      action: 'Gán cho yêu cầu',
      title: 'Gán cho yêu cầu thanh toán',
      description: 'Giao dịch thanh toán cho yêu cầu: được quyết toán khi yêu cầu còn mở, còn hạn và số tiền khớp; nếu không, giao dịch chờ xem xét kèm lý do.',
      code: 'Mã nội dung chuyển khoản',
      codeHint: 'Mã của yêu cầu, ví dụ TINO7K3QX9AB.',
      codeInvalid: 'Không phải mã nội dung chuyển khoản.',
      submit: 'Gán',
    },
    credit: {
      action: 'Cộng vào ví',
      title: 'Cộng vào ví khách hàng',
      description: 'Toàn bộ số tiền được cộng vào ví: vào yêu cầu mà giao dịch ghi mã nếu yêu cầu còn mở, nếu không thì thành một lần nạp ví của khách hàng.',
      customer: 'Mã khách hàng (không bắt buộc)',
      customerHint: 'Để trống: khách hàng của yêu cầu mà giao dịch ghi mã.',
      submit: 'Cộng vào ví',
    },
    dismiss: {
      action: 'Bỏ qua',
      title: 'Bỏ qua giao dịch',
      description: 'Không phải tiền khách hàng thanh toán (ví dụ chuyển giữa các tài khoản của công ty). Nếu cần hoàn lại cho người chuyển, làm tại ngân hàng.',
      submit: 'Bỏ qua',
    },
    settlement: {
      action: 'Tiền quyết toán từ cổng',
      title: 'Tiền quyết toán từ cổng thanh toán',
      description: 'Khoản cổng thanh toán chuyển trả tiền đã thu của khách vào tài khoản công ty. Tiền của từng khách đã vào ví khi cổng báo thanh toán, nên khoản này chỉ được ghi nhận, không cộng ví nào.',
      module: 'Cổng thanh toán',
      submit: 'Ghi nhận',
    },
    result: { SETTLED: 'Đã quyết toán.', REVIEW: 'Giao dịch chờ xem xét: {{reason}}.', IGNORED: 'Đã bỏ qua giao dịch.', UNMATCHED: 'Chưa khớp.' },
  },
  qr: {
    topUp: {
      action: 'Mã QR nạp ví',
      title: 'Mã QR nạp ví',
      description: 'Mã để {{customer}} chuyển khoản vào tài khoản nhận tiền; tiền được cộng vào ví khi nguồn giao dịch báo về.',
      amount: 'Số tiền (VND)',
      amountHint: 'Số đồng chẵn.',
      amountInvalid: 'Nhập số đồng chẵn lớn hơn 0.',
      submit: 'Tạo mã',
    },
    order: {
      action: 'Mã QR thanh toán',
      title: 'Mã QR thanh toán đơn hàng',
      description: 'Mã cho phần tiền ví chưa đủ; đơn hàng được xử lý tiếp ngay khi tiền về.',
      submit: 'Tạo mã',
      funded: 'Ví đã đủ tiền: đơn hàng đã được tạm giữ tiền, không cần chuyển khoản.',
      notAwaiting: 'Đơn hàng này không chờ chuyển khoản.',
    },
    invoice: {
      action: 'Mã QR thanh toán',
      title: 'Mã QR cho hoá đơn',
      description: 'Mã chuyển khoản cho đơn hàng của hoá đơn khi đơn hàng còn chờ thanh toán.',
      notAwaiting: 'Đơn hàng của hoá đơn này đã được thanh toán. Có thể tạo mã nạp ví bằng tổng tiền hoá đơn.',
      topUp: 'Tạo mã nạp ví {{amount}}',
    },
    shown: {
      account: 'Số tài khoản',
      bank: 'Ngân hàng',
      holder: 'Chủ tài khoản',
      amount: 'Số tiền',
      content: 'Nội dung chuyển khoản',
      expires: 'Hiệu lực đến',
      open: 'Xem yêu cầu thanh toán',
      contentHint: 'Giữ nguyên nội dung chuyển khoản này để tiền được tự động ghi nhận.',
      image: 'Mã QR',
    },
    unavailable: 'Chưa có tài khoản hiện được trên mã QR chuyển khoản (Cài đặt › Module › Thanh toán).',
  },
  refund: {
    title: 'Hoàn tiền',
    titleFor: 'Hoàn tiền cho {{domain}}',
    loadError: 'Không tải được khoản hoàn tiền',
    field: { id: 'Mã khoản hoàn tiền', reason: 'Lý do', itemStatus: 'Trạng thái tên miền', entry: 'Bút toán đảo', payment: 'Hoàn qua', created: 'Ngày tạo' },
    stat: { status: 'Trạng thái hoàn tiền', amount: 'Số tiền đã hoàn' },
    fromWallet: 'Hoàn lại vào ví khách hàng',
    entry: 'Tiền đã được hoàn thế nào',
    noEntry: 'Chưa ghi nhận bút toán cho khoản hoàn tiền này.',
    creditNotes: 'Phiếu điều chỉnh đã phát hành',
    noCreditNotes: 'Chưa phát hành phiếu điều chỉnh cho khoản hoàn tiền này.',
  },
  invoices: {
    title: 'Hoá đơn',
    subtitle: 'Hoá đơn và phiếu điều chỉnh đã phát hành cho khách hàng, đánh số liên tục theo từng ký hiệu.',
    limitTitle: 'Hoá đơn điện tử phát hành qua module',
    limit: 'Mỗi hoá đơn có một hoá đơn điện tử, phát hành qua nhà cung cấp đang bật ở Cài đặt › Module › Hoá đơn ĐT (tự động hoặc do nhân viên bấm, theo cài đặt). Mở một hoá đơn để xem số, mã tra cứu và mã của cơ quan thuế; điều chỉnh, thay thế và huỷ cần người thứ hai duyệt.',
    documentFilter: 'Loại chứng từ',
    seriesFilter: 'Ký hiệu',
    seriesPlaceholder: 'vd. INV',
    loadError: 'Không tải được danh sách hoá đơn',
    empty: 'Không có hoá đơn phù hợp',
    col: { number: 'Số', document: 'Chứng từ', customer: 'Khách hàng', order: 'Đơn hàng', subtotal: 'Tạm tính', tax: 'Thuế', total: 'Tổng cộng', issued: 'Ngày phát hành' },
  },
  invoice: {
    title: 'Hoá đơn',
    loadError: 'Không tải được hoá đơn',
    field: { document: 'Chứng từ', number: 'Số', credits: 'Điều chỉnh cho hoá đơn' },
    immutable: 'Chứng từ đã phát hành không bao giờ được sửa; điều chỉnh bằng phiếu điều chỉnh (credit note).',
    lines: 'Dòng hàng',
    lineCol: { description: 'Diễn giải', qty: 'SL', unitPrice: 'Đơn giá', amount: 'Thành tiền', tax: 'Thuế', taxAmount: 'Tiền thuế' },
    credit: '(ghi giảm)',
    creditedInvoice: 'Hoá đơn được điều chỉnh',
    creditNotes: 'Phiếu điều chỉnh',
    buyer: {
      title: 'Người mua',
      description: 'Theo thông tin của khách hàng tại thời điểm phát hành; sửa thông tin khách hàng sau đó không làm thay đổi chứng từ này.',
      name: 'Tên người mua',
      taxCode: 'Mã số thuế',
      identityNumber: 'Số định danh',
      address: 'Địa chỉ',
      emails: 'E-mail nhận hoá đơn',
      none: 'Phát hành trước khi hoá đơn lưu thông tin người mua.',
    },
  },
  einvoice: {
    title: 'Hoá đơn điện tử',
    description: 'Phát hành qua {{provider}}',
    mode: {
      title: 'Cách phát hành:',
      delivery: 'Gửi cho người mua:',
      unreadable: 'không đọc được cài đặt chung (không phát hành gì cho đến khi sửa)',
      help: {
        AUTOMATIC: 'Mỗi hoá đơn được xuất từ khi cài đặt này có hiệu lực được hệ thống tự phát hành hoá đơn điện tử (trong khoảng một phút).',
        DAILY: 'Lượt chạy 17:00 hằng ngày khởi tạo và ký hoá đơn điện tử của các bản đối soát xuất trước 17:00, cũ trước mới sau, mang ngày của bản đối soát; ngoài lượt chạy không gửi gì.',
        MANUAL: 'Hoá đơn điện tử nháp chờ nhân viên bấm "Phát hành"; hệ thống không tự phát hành.',
      },
    },
    draftHelp: {
      AUTOMATIC: 'Chưa gửi cho nhà cung cấp. Hoá đơn được xuất khi đang phát hành tự động: hệ thống tự phát hành ở lần chạy kế tiếp (có thể bấm "Phát hành" ngay).',
      DAILY: 'Chờ lượt chạy 17:00: hệ thống khởi tạo và ký trên nhà cung cấp, ngày lập là ngày của bản đối soát (điều chỉnh, thay thế: ngày chạy). Khách cần sửa thông tin người mua thì báo trước 17:00; sửa xong bấm "Lấy lại người mua".',
      MANUAL: 'Chưa gửi cho nhà cung cấp. Bấm "Phát hành": hệ thống không tự phát hành hoá đơn này, kể cả khi cài đặt chuyển sang tự động.',
    },
    heldUntil: 'Đang giữ lại vì thông tin người mua vừa được sửa: đi lượt chạy {{at}}, vẫn mang ngày của bản đối soát.',
    loadError: 'Không tải được hoá đơn điện tử',
    none: 'Hoá đơn này chưa có hoá đơn điện tử.',
    creditNote: 'Phiếu điều chỉnh không có hoá đơn điện tử riêng: hoá đơn điện tử của hoá đơn gốc được điều chỉnh giảm bằng phiếu này (người thứ hai duyệt).',
    noProviderTitle: 'Chưa bật nhà cung cấp hoá đơn điện tử',
    noProvider: 'Chưa có kết nối nào đang bật ở mảng Hoá đơn ĐT, nên chưa phát hành được; hoá đơn điện tử nháp chờ đến khi bật.',
    settingsLink: 'Cài đặt › Module › Hoá đơn ĐT',
    settingsHint: 'Nhà cung cấp, mẫu số, ký hiệu và cách phát hành (lượt chạy 17:00, tự động hay do nhân viên bấm) nằm ở',
    carries: 'mang phiếu điều chỉnh',
    taxCode: 'MST',
    identity: 'Số định danh',
    checkedAt: 'đọc lúc {{at}}',
    system: 'Hệ thống',
    providerCode: 'mã của nhà cung cấp {{code}}',
    people: 'Đề nghị: {{asker}} · Duyệt: {{approver}}',
    history: 'Lịch sử ({{count}})',
    cancellation: 'Huỷ hoá đơn:',
    openCreditNotes: 'Phiếu điều chỉnh chưa đưa lên hoá đơn điện tử ({{count}}):',
    withdrawHelp: 'Bỏ hoá đơn điều chỉnh hoặc thay thế chưa phát hành được. Hoá đơn gốc giữ nguyên; có thể đề nghị điều chỉnh khác.',
    close: 'Đóng',
    field: {
      number: 'Ký hiệu · Số hoá đơn',
      template: 'Mẫu số',
      issuedAt: 'Ngày phát hành',
      lookupCode: 'Mã tra cứu',
      taxAuthorityCode: 'Mã của cơ quan thuế',
      authority: 'Cơ quan thuế',
      buyer: 'Người mua',
      attempts: 'Số lần gửi',
      provider: 'Kết nối',
      reason: 'Lý do',
      content: 'Nội dung điều chỉnh',
      people: 'Người đề nghị và người duyệt',
      delivery: 'Gửi cho người mua',
    },
    stateHelp: {
      DRAFT: 'Chưa gửi cho nhà cung cấp. Bấm "Phát hành" (hoặc chờ tự động, theo cài đặt).',
      ISSUING: 'Đang gọi nhà cung cấp. Nếu quá lâu không có kết quả, hệ thống tra cứu theo khoá để biết đã phát hành hay chưa.',
      ISSUED: 'Đã phát hành. Người mua tra cứu bằng mã tra cứu trên trang của nhà cung cấp.',
      FAILED: 'Chưa phát hành được (không có hoá đơn nào được lập dưới khoá này). Sửa nguyên nhân rồi gửi lại.',
      NEEDS_RECONCILIATION: 'Chưa rõ nhà cung cấp đã lập hoá đơn chưa. Hệ thống tra cứu theo khoá trước khi làm gì khác: không bao giờ lập hai lần.',
      REPLACED: 'Đã bị thay thế bởi hoá đơn thay thế bên dưới.',
      CANCELLED: 'Đã huỷ ở nhà cung cấp.',
      WITHDRAWN: 'Đã bỏ, chưa từng được phát hành.',
    },
    why: {
      FAILED: 'Vì sao chưa phát hành được',
      NEEDS_RECONCILIATION: 'Đang chờ tra cứu theo khoá',
      other: 'Cần chú ý',
    },
    problem: {
      CURRENCY_NOT_SUPPORTED: 'Chỉ phát hành hoá đơn điện tử bằng VND.',
      NO_LINES: 'Chứng từ không có dòng hàng.',
      LINE_NAME: 'Một dòng hàng không có tên (hoặc quá dài).',
      LINE_QUANTITY: 'Số lượng của một dòng hàng không hợp lệ.',
      LINE_AMOUNT: 'Thành tiền của một dòng hàng không phải số nguyên đồng hoặc không khớp đơn giá.',
      LINE_VAT_UNKNOWN: 'Một dòng hàng chưa có loại thuế (VAT hoặc không chịu thuế).',
      LINE_VAT_RATE: 'Thuế suất của một dòng hàng không được hỗ trợ.',
      LINE_VAT_AMOUNT: 'Tiền thuế của một dòng hàng không khớp thuế suất.',
      TOTALS_MISMATCH: 'Tổng tiền không khớp với các dòng hàng.',
      TOTAL_IN_WORDS: 'Không đọc được tổng tiền bằng chữ.',
      BUYER_NAME_MISSING: 'Thiếu tên người mua.',
      BUYER_TOO_LONG: 'Tên hoặc địa chỉ người mua quá dài.',
      BUYER_TAX_CODE_MISSING: 'Tổ chức chưa có mã số thuế: cập nhật thông tin xuất hoá đơn của khách hàng rồi bấm "Lấy lại người mua".',
      BUYER_TAX_CODE_INVALID: 'Mã số thuế người mua không hợp lệ.',
      BUYER_IDENTITY_INVALID: 'Số định danh của người mua không hợp lệ (CCCD 12 chữ số).',
      BUYER_IDENTITY_UNREADABLE: 'Không giải mã được số định danh của người mua (khoá hoặc ngữ cảnh).',
      BUYER_ADDRESS_MISSING: 'Thiếu địa chỉ người mua.',
      PAYMENT_METHOD: 'Hình thức thanh toán trong cài đặt không hợp lệ.',
      NOTE_MISSING: 'Hoá đơn điều chỉnh thông tin cần nội dung điều chỉnh.',
    },
    refusal: {
      NOT_FOUND: 'Nhà cung cấp không tìm thấy hoá đơn.',
      ALREADY_EXISTS: 'Hoá đơn đã có ở nhà cung cấp.',
      UNAUTHORIZED: 'Sai tài khoản hoặc mật khẩu kết nối nhà cung cấp.',
      IP_NOT_ALLOWED: 'Nhà cung cấp không cho máy chủ này kết nối (IP).',
      INVALID_REQUEST: 'Nhà cung cấp từ chối nội dung hoá đơn.',
      CONFLICT: 'Nhà cung cấp báo xung đột với dữ liệu đang có.',
      QUOTA_EXCEEDED: 'Đã vượt hạn mức ở nhà cung cấp.',
      INSUFFICIENT_FUNDS: 'Tài khoản ở nhà cung cấp không đủ số dư.',
      RATE_LIMITED: 'Gọi nhà cung cấp quá nhanh; sẽ thử lại.',
      MAINTENANCE: 'Nhà cung cấp đang bảo trì.',
      UNSUPPORTED: 'Nhà cung cấp không hỗ trợ việc này.',
      BUYER_TAX_CODE_INVALID: 'Nhà cung cấp từ chối mã số thuế người mua.',
      TEMPLATE_NOT_FOUND: 'Mẫu số hoặc ký hiệu chưa đăng ký cho công ty ở nhà cung cấp.',
      DUPLICATE_KEY: 'Khoá đã được dùng ở nhà cung cấp: hệ thống tra cứu theo khoá.',
      NOT_ISSUABLE: 'Nhà cung cấp không lập được hoá đơn với nội dung này.',
      TAX_AUTHORITY_REJECTED: 'Cơ quan thuế từ chối hoá đơn.',
      NUMBERS_EXHAUSTED: 'Ký hiệu đã hết số hoá đơn.',
      CERTIFICATE_EXPIRED: 'Chứng thư số ký hoá đơn của nhà cung cấp đã hết hạn.',
      DATE_BEFORE_LAST_ISSUED: 'Ngày hoá đơn trước ngày của hoá đơn phát hành gần nhất.',
      ALREADY_PROCESSED: 'Hoá đơn đã được điều chỉnh, thay thế hoặc huỷ ở nhà cung cấp.',
      STATE_NOT_ALLOWED: 'Trạng thái hoá đơn ở nhà cung cấp không cho làm việc này.',
      PROVIDER_FAILURE: 'Nhà cung cấp báo lỗi phía họ.',
      NUMBERING_FAILED: 'Nhà cung cấp không cấp được số hoá đơn.',
      UNDOCUMENTED: 'Nhà cung cấp trả về mã không có trong tài liệu của họ.',
      TAX_AUTHORITY_PENDING: 'Cơ quan thuế chưa cấp mã cho hoá đơn.',
      NO_ANSWER: 'Gửi hết số lần mà không có câu trả lời, và nhà cung cấp chưa có hoá đơn theo khoá: kiểm tra nhà cung cấp rồi gửi lại.',
      DOCUMENT_INVALID: 'Chứng từ chưa đủ thông tin để lập hoá đơn điện tử.',
      other: 'Nhà cung cấp từ chối ({{code}}).',
    },
    reconcile: {
      UNKNOWN: 'Không nhận được câu trả lời của nhà cung cấp.',
      DUPLICATE_KEY: 'Nhà cung cấp báo khoá đã dùng.',
      PROVIDER_FAILURE: 'Nhà cung cấp báo lỗi phía họ sau khi nhận yêu cầu.',
      NUMBERING_FAILED: 'Nhà cung cấp báo lỗi cấp số sau khi nhận yêu cầu.',
      STALE: 'Lần gọi trước bị gián đoạn giữa chừng.',
    },
    action: {
      CREATE: 'Tạo hoá đơn điện tử',
      ISSUE: 'Phát hành',
      RETRY: 'Gửi lại',
      RECONCILE: 'Tra cứu theo khoá',
      REFRESH_BUYER: 'Lấy lại người mua',
      STATUS: 'Đọc kết quả của cơ quan thuế',
      DOWNLOAD: 'Tải hoá đơn',
      DOWNLOAD_PDF: 'Tải PDF',
      DOWNLOAD_XML: 'Tải XML',
      ADJUST_DECREASE: 'Điều chỉnh giảm',
      ADJUST_INCREASE: 'Điều chỉnh tăng',
      ADJUST_INFORMATION: 'Điều chỉnh thông tin',
      REPLACE: 'Thay thế',
      CANCEL: 'Huỷ hoá đơn',
      WITHDRAW: 'Bỏ hoá đơn chưa phát hành',
    },
    change: {
      reason: 'Lý do (ghi trên hoá đơn và trong nhật ký)',
      creditNote: 'Phiếu điều chỉnh (credit note) đưa lên hoá đơn điều chỉnh',
      before: 'Nội dung đang ghi trên hoá đơn',
      beforeHint: 'Không bắt buộc',
      after: 'Nội dung đúng',
      buyerFromAccount: 'Người mua lấy từ thông tin xuất hoá đơn hiện tại của khách hàng; nếu thông tin đó đổi trước khi duyệt xong, cần đề nghị lại.',
      fourEyes: 'Một người khác có quyền quản lý hoá đơn điện tử phải duyệt trước khi thực hiện.',
      submit: 'Gửi đề nghị duyệt',
      requestedTitle: 'Đã gửi đề nghị',
      requested: 'Một người khác duyệt ở trang',
      approvals: 'Phê duyệt',
      help: {
        ADJUST_DECREASE: 'Lập hoá đơn điều chỉnh giảm mang các dòng của phiếu điều chỉnh đã phát hành.',
        ADJUST_INFORMATION: 'Lập hoá đơn điều chỉnh thông tin (không đổi số tiền), ví dụ địa chỉ hoặc tên người mua.',
        REPLACE: 'Lập hoá đơn thay thế với cùng các dòng hàng và người mua theo thông tin hiện tại; hoá đơn này chuyển sang "đã bị thay thế".',
        CANCEL: 'Huỷ hoá đơn ở nhà cung cấp. Chỉ dùng khi hoá đơn lập nhầm và chưa giao dịch.',
      },
    },
    pending: {
      title: 'Đề nghị đang chờ',
      expires: 'hết hạn {{at}}',
      asked: 'Đề nghị: {{name}}, {{at}}',
      approved: 'Đã duyệt: {{name}}',
      execute: 'Thực hiện',
      waiting: 'Đang chờ người thứ hai duyệt ở trang',
    },
    event: {
      CREATED: 'Tạo hoá đơn điện tử nháp',
      BUYER_REFRESHED: 'Lấy lại người mua',
      BUILD_FAILED: 'Chưa lập được hoá đơn (thiếu thông tin)',
      ISSUE_SENT: 'Gửi phát hành',
      ISSUED: 'Đã phát hành',
      NOT_SENT: 'Chưa gửi được, sẽ thử lại',
      NOT_SENT_EXHAUSTED: 'Thử lại hết số lần, chưa gửi được',
      REFUSED: 'Nhà cung cấp từ chối',
      REFUSED_UNSETTLED: 'Nhà cung cấp báo lỗi, cần tra cứu theo khoá',
      UNKNOWN: 'Không rõ kết quả, cần tra cứu theo khoá',
      STALE: 'Lần gọi bị gián đoạn, cần tra cứu theo khoá',
      KEY_NOT_FOUND: 'Tra cứu: nhà cung cấp chưa có hoá đơn theo khoá',
      RECONCILED_PRESENT: 'Tra cứu: hoá đơn đã được phát hành',
      RECONCILED_ABSENT: 'Tra cứu: chưa có hoá đơn, gửi lại cùng khoá',
      RECONCILE_FAILED: 'Tra cứu không thành công',
      ATTENTION: 'Cần nhân viên xem',
      STATUS: 'Kết quả của cơ quan thuế',
      DELIVERED: 'Đã gửi cho người mua',
      DELIVERY_FAILED: 'Gửi cho người mua không thành công',
      DELIVERY_BY_PROVIDER: 'Nhà cung cấp tự gửi e-mail cho người mua',
      DELIVERY_SKIPPED: 'Không gửi cho người mua',
      MARKED_PAID: 'Đánh dấu đã thanh toán',
      MARK_PAID_FAILED: 'Đánh dấu đã thanh toán không thành công',
      CHANGE_PROPOSED: 'Đề nghị điều chỉnh, thay thế hoặc huỷ',
      CHANGE_APPROVED: 'Đề nghị đã được duyệt và thực hiện',
      CHANGE_WITHDRAWN: 'Bỏ đề nghị',
      ADJUSTED: 'Đã được điều chỉnh',
      REPLACED: 'Đã bị thay thế',
      WITHDRAWN: 'Đã bỏ',
      CANCEL_SENT: 'Gửi yêu cầu huỷ',
      CANCEL_CHECK: 'Kiểm tra lại yêu cầu huỷ',
      CANCEL_DONE: 'Đã huỷ ở nhà cung cấp',
      CANCEL_REFUSED: 'Nhà cung cấp từ chối huỷ',
      CANCEL_NOT_SENT: 'Chưa gửi được yêu cầu huỷ, sẽ thử lại',
      CANCEL_NOT_SENT_EXHAUSTED: 'Thử lại hết số lần, chưa gửi được yêu cầu huỷ',
      CANCEL_UNKNOWN: 'Không rõ kết quả huỷ, sẽ kiểm tra lại',
      CANCELLED: 'Đã huỷ',
    },
  },
  pricing: {
    title: 'Bảng giá',
    subtitle: 'Giá vốn của từng tên miền và giá bán ra. Giá không bao giờ bị ghi đè: phiên bản mới sẽ đóng phiên bản trước.',
    validity: 'Hiệu lực',
    inForce: 'Đang hiệu lực',
    withHistory: 'Kèm lịch sử',
    loadError: 'Không tải được bảng giá',
    stat: { registryPrices: 'Giá registry', priceBooks: 'Bảng giá bán', fxRates: 'Tỷ giá', taxRates: 'Thuế suất' },
    registryCost: 'Giá vốn registry',
    noRegistryPrices: 'Chưa ghi nhận giá registry.',
    registryCol: { tld: 'TLD', registry: 'Registry', operation: 'Nghiệp vụ', years: 'Số năm', cost: 'Giá vốn', version: 'Phiên bản', validity: 'Hiệu lực' },
    priceBooks: 'Bảng giá bán',
    noPriceBooks: 'Chưa có bảng giá.',
    bookTier: 'hạng {{tier}}',
    bookCustomer: 'khách hàng {{name}}',
    openBook: 'Mở bảng giá',
    noRules: 'Chưa có quy tắc.',
    noRulesInForce: 'Không có quy tắc đang hiệu lực.',
    forTld: 'Bảng giá .{{zone}}',
    allTldsPrices: 'Bảng giá tất cả TLD',
    ruleCol: { operation: 'Nghiệp vụ', tld: 'TLD', years: 'Số năm', rule: 'Quy tắc', value: 'Giá trị', bounds: 'Giới hạn', priority: 'Ưu tiên', validity: 'Hiệu lực' },
    allTlds: 'tất cả',
    perYear: 'mỗi năm',
    min: 'tối thiểu',
    max: 'tối đa',
    fxRates: 'Tỷ giá',
    noFx: 'Chưa ghi nhận tỷ giá.',
    fxCol: { pair: 'Cặp tiền', rate: 'Tỷ giá', source: 'Lấy từ', effective: 'Hiệu lực từ' },
    taxRates: 'Thuế suất',
    noTax: 'Chưa ghi nhận thuế suất: sản phẩm chưa báo giá và mở bán được cho tới khi có thuế suất.',
    taxDefault: 'Mặc định',
    taxDefaultIs: 'Giá tên miền và add-on không ghi mã thuế tính theo {{code}}.',
    taxNoDefault: 'Chưa có mã thuế mặc định: giá tên miền và add-on không ghi mã thuế sẽ không báo giá được.',
    taxCol: { jurisdiction: 'Khu vực thuế', code: 'Mã', rate: 'Thuế suất', source: 'Căn cứ', validity: 'Hiệu lực' },
    window: { SCHEDULED: 'Sắp áp dụng', CLOSED: 'Đã hết hiệu lực', IN_FORCE: 'Đang hiệu lực' },
    from: 'từ {{at}}',
    until: 'đến {{at}}',
    open: 'không thời hạn',
  },
  priceBook: {
    title: 'Bảng giá bán',
    loadError: 'Không tải được bảng giá',
    field: { code: 'Mã', appliesTo: 'Áp dụng cho', tier: 'Hạng', customer: 'Khách hàng', created: 'Ngày tạo' },
    stat: { rules: 'Quy tắc', quotes: 'Báo giá đã dùng', lastQuoted: 'Lần dùng gần nhất' },
    never: 'Chưa dùng lần nào',
    rules: 'Giá bán theo TLD',
    allRules: 'Hiển thị cả khoảng đã hết hiệu lực: quy tắc được thay thế, không bị sửa.',
    noRules: 'Bảng giá này chưa có quy tắc nào nên không thể tính giá.',
  },
  promotions: {
    title: 'Khuyến mãi',
    subtitle: 'Các mã giảm giá dành cho khách hàng: thời gian áp dụng và số lượt đã dùng.',
    stateFilter: 'Tình trạng',
    loadError: 'Không tải được danh sách khuyến mãi',
    empty: 'Không có khuyến mãi phù hợp',
    col: { code: 'Mã', discount: 'Mức giảm', appliesTo: 'Áp dụng cho', state: 'Tình trạng', window: 'Thời gian', redemptions: 'Lượt dùng' },
    anyOperation: 'Mọi nghiệp vụ',
    allTlds: 'mọi TLD',
    filterBy: 'Xem khuyến mãi {{state}}',
  },
  promotion: {
    title: 'Khuyến mãi',
    loadError: 'Không tải được khuyến mãi',
    field: { code: 'Mã', created: 'Ngày tạo', starts: 'Bắt đầu', ends: 'Kết thúc', limit: 'Giới hạn lượt dùng', noLimit: 'Không giới hạn' },
    stat: { state: 'Tình trạng', discount: 'Mức giảm', redemptions: 'Lượt dùng', left: 'còn {{count}} lượt' },
    usage: 'Báo giá đã dùng mã này',
    usageNote: 'Mức giảm được lưu trong từng báo giá, nên số tiền khách đã trả vẫn chứng minh được sau khi mã kết thúc.',
    noUsage: 'Mã này chưa được dùng trong báo giá nào.',
    usageCol: { domain: 'Tên miền', customer: 'Khách hàng', discount: 'Mức giảm', total: 'Tổng báo giá', order: 'Đơn hàng', created: 'Ngày báo giá' },
    notOrdered: 'Chưa đặt hàng',
  },
  enum: {
    orderStatus: { PLACED: 'Đã đặt', FUNDS_RESERVED: 'Đã tạm giữ tiền', FULFILLING: 'Đang xử lý', COMPLETED: 'Hoàn tất', PARTIALLY_COMPLETED: 'Hoàn tất một phần', FAILED: 'Thất bại', CANCELLED: 'Đã huỷ' },
    orderItemStatus: { PENDING: 'Đang chờ', FUNDS_RESERVED: 'Đã tạm giữ tiền', FULFILLING: 'Đang xử lý', FULFILLED: 'Hoàn tất', FAILED: 'Thất bại', REFUNDED: 'Đã hoàn tiền', CANCELLED: 'Đã huỷ' },
    holdStatus: { ACTIVE: 'Đang giữ', CAPTURED: 'Đã quyết toán', RELEASED: 'Đã giải phóng', EXPIRED: 'Hết hạn' },
    paymentStatus: { INITIATED: 'Khởi tạo', PENDING: 'Đang chờ', SUCCEEDED: 'Thành công', FAILED: 'Thất bại', UNKNOWN: 'Chưa rõ — cần đối soát', CANCELLED: 'Đã huỷ' },
    refundStatus: { COMPLETED: 'Hoàn tất', PENDING_PROVIDER: 'Chờ nhà cung cấp', FAILED: 'Thất bại' },
    sourceRefundStatus: { PENDING_PROVIDER: 'Chờ cổng xử lý', COMPLETED: 'Đã hoàn', FAILED: 'Không thành công (đã trả lại ví)' },
    attemptStatus: {
      CREATED: 'Mới tạo',
      PENDING: 'Khách đang ở trang cổng',
      REQUIRES_ACTION: 'Chờ khách xác thực',
      AUTHORIZED: 'Đã giữ tiền, chưa thu',
      CAPTURED: 'Đã thanh toán',
      FAILED: 'Thất bại',
      CANCELLED: 'Đã huỷ',
      EXPIRED: 'Hết hạn',
      UNKNOWN: 'Chưa rõ — đang tra cổng',
    },
    docType: { INVOICE: 'Hoá đơn', CREDIT_NOTE: 'Phiếu điều chỉnh (credit note)' },
    einvoiceState: { DRAFT: 'Nháp', ISSUING: 'Đang phát hành', ISSUED: 'Đã phát hành', FAILED: 'Chưa phát hành được', NEEDS_RECONCILIATION: 'Chờ tra cứu', REPLACED: 'Đã bị thay thế', CANCELLED: 'Đã huỷ', WITHDRAWN: 'Đã bỏ' },
    einvoiceKind: { ORIGINAL: 'Hoá đơn gốc', ADJUSTMENT: 'Hoá đơn điều chỉnh', REPLACEMENT: 'Hoá đơn thay thế' },
    einvoiceAdjust: { INCREASE: 'Điều chỉnh tăng', DECREASE: 'Điều chỉnh giảm', INFORMATION: 'Điều chỉnh thông tin' },
    einvoiceAuthority: { DRAFT: 'Nháp', ISSUED: 'Chờ cơ quan thuế', SENT_TO_TAX_AUTHORITY: 'Đã gửi cơ quan thuế', ACCEPTED_BY_TAX_AUTHORITY: 'Cơ quan thuế đã cấp mã', REJECTED_BY_TAX_AUTHORITY: 'Cơ quan thuế từ chối', ADJUSTED: 'Đã bị điều chỉnh', REPLACED: 'Đã bị thay thế', CANCELLED: 'Đã huỷ' },
    einvoiceCancel: { QUEUED: 'Chờ gửi', SENDING: 'Đang gửi', DONE: 'Đã huỷ', REFUSED: 'Bị từ chối', UNKNOWN: 'Chưa rõ kết quả', FAILED: 'Chưa gửi được' },
    einvoiceApproval: { PENDING: 'Chờ duyệt', APPROVED: 'Đã duyệt' },
    einvoiceMode: { MANUAL: 'Nhân viên bấm phát hành', AUTOMATIC: 'Tự động phát hành khi hoá đơn được xuất', DAILY: 'Lượt chạy 17:00 hằng ngày' },
    einvoiceDelivery: { PROVIDER: 'Nhà cung cấp tự gửi e-mail', SENT: 'Nhà cung cấp đã gửi', FAILED: 'Gửi không thành công', SKIPPED: 'Không gửi' },
    einvoiceDeliverySetting: { PROVIDER: 'Nhà cung cấp hoá đơn tự gửi e-mail cho người mua', NONE: 'Không gửi cho người mua' },
    accountStatus: { OPEN: 'Đang mở', FROZEN: 'Đóng băng', CLOSED: 'Đã đóng' },
    serviceAction: { NEW: 'Mua mới', RENEW: 'Gia hạn', CHANGE_PLAN: 'Đổi gói', CHANGE_OPTIONS: 'Đổi cấu hình', ADDON: 'Tiện ích' },
    operation: { REGISTER: 'Đăng ký mới', RENEW: 'Gia hạn', TRANSFER: 'Chuyển nhà đăng ký', RESTORE: 'Khôi phục', REDEMPTION: 'Chuộc lại', ADDON: 'Dịch vụ thêm', BACKORDER: 'Đặt giữ', AUCTION: 'Đấu giá' },
    holdPurpose: { REGISTRATION: 'Đăng ký mới', RENEWAL: 'Gia hạn', TRANSFER: 'Chuyển nhà đăng ký', RESTORE: 'Khôi phục', AUCTION: 'Đấu giá', DROPCATCH: 'Săn tên miền hết hạn', OTHER: 'Khác' },
    paymentPurpose: { WALLET_TOPUP: 'Nạp ví', ORDER: 'Thanh toán đơn hàng' },
    bankTxFilter: { OPEN: 'Cần xử lý', UNMATCHED: 'Chưa khớp', REVIEW: 'Chờ xem xét', SETTLED: 'Đã quyết toán', IGNORED: 'Đã bỏ qua' },
    bankTxStatus: { UNMATCHED: 'Chưa khớp', REVIEW: 'Chờ xem xét', SETTLED: 'Đã quyết toán', IGNORED: 'Đã bỏ qua' },
    bankTxReason: {
      NO_CODE: 'Không có mã trong nội dung',
      UNKNOWN_CODE: 'Mã không thuộc yêu cầu nào',
      DUPLICATE_REFERENCE: 'Trùng mã giao dịch đã ghi nhận',
      SHARED_ACCOUNT: 'Tài khoản có hai nguồn báo',
      AMOUNT_SHORT: 'Thiếu so với yêu cầu',
      AMOUNT_OVER: 'Thừa so với yêu cầu',
      REQUEST_EXPIRED: 'Chuyển sau khi yêu cầu hết hạn',
      REQUEST_CLOSED: 'Yêu cầu đã huỷ',
      ALREADY_PAID: 'Yêu cầu đã được thanh toán',
      OTHER_ACCOUNT: 'Chuyển vào tài khoản khác',
      MATCHED: 'Tự động khớp',
      ASSIGNED: 'Nhân viên gán',
      CREDITED: 'Cộng vào ví',
      CONFIRMED: 'Nhân viên xác nhận',
      OUTGOING: 'Tiền ra',
      DISMISSED: 'Nhân viên bỏ qua',
      SETTLEMENT_PAYOUT: 'Tiền quyết toán từ cổng',
    },
    txDirection: { IN: 'Tiền vào', OUT: 'Tiền ra' },
    approvalStatus: { PENDING: 'Chờ phê duyệt', APPROVED: 'Đã phê duyệt', REJECTED: 'Bị từ chối', EXECUTED: 'Đã sử dụng', EXPIRED: 'Hết hạn', CANCELLED: 'Đã huỷ' },
    orderFunding: { FUNDED: 'Đơn hàng được xử lý tiếp', NOT_WAITING: 'Đơn hàng không chờ thanh toán', INSUFFICIENT: 'Đơn hàng vẫn chờ (ví chưa đủ)' },
    bankFeed: { sepay: 'SePay', casso: 'Casso', tinopay: 'TinoPay', vnpay: 'VNPAY', fake: 'Nguồn giả lập', staff: 'Nhân viên (thủ công)' },
    accountType: { ASSET: 'Tài sản', LIABILITY: 'Nợ phải trả', EQUITY: 'Vốn chủ sở hữu', REVENUE: 'Doanh thu', EXPENSE: 'Chi phí' },
    accountKind: {
      WALLET: 'Ví khách hàng',
      CLEARING: 'Tài khoản trung gian cổng thanh toán',
      REVENUE: 'Doanh thu',
      COST_OF_SALES: 'Giá vốn hàng bán',
      REGISTRY_PAYABLE: 'Phải trả registry',
      REFUNDS_PAYABLE: 'Phải trả hoàn tiền',
      TAX_PAYABLE: 'Thuế phải nộp',
      OTHER: 'Tài khoản sổ cái',
    },
    ownerType: { CUSTOMER: 'Khách hàng', REGISTRY: 'Registry', PLATFORM: 'Nền tảng' },
    entryType: { WALLET_TOPUP: 'Nạp ví', HOLD_CAPTURE: 'Quyết toán tạm giữ', REGISTRY_COST_ACCRUAL: 'Trích trước giá vốn registry', REVERSAL: 'Bút toán đảo', REFUND: 'Hoàn tiền', WALLET_ADJUSTMENT: 'Điều chỉnh số dư', WALLET_PAYOUT: 'Hoàn tiền về nguồn' },
    resourceType: { payment: 'Thanh toán', balance_hold: 'Khoản tạm giữ', order_item: 'Tên miền đã đặt', journal_entry: 'Bút toán', refund: 'Hoàn tiền', customer: 'Khách hàng' },
    ruleType: { MARKUP_PERCENT: 'Cộng thêm (%)', MARKUP_FIXED: 'Cộng thêm cố định', FIXED_PRICE: 'Giá cố định' },
    appliesTo: { TIER: 'Theo hạng', CUSTOMER_CONTRACT: 'Giá riêng của khách hàng' },
    bookStatus: { DRAFT: 'Bản nháp', ACTIVE: 'Đang áp dụng', RETIRED: 'Ngừng áp dụng' },
    promoState: { ACTIVE: 'Đang chạy', SCHEDULED: 'Sắp diễn ra', EXHAUSTED: 'Hết lượt', ENDED: 'Đã kết thúc' },
    actorType: { CUSTOMER: 'Khách hàng', OPERATOR: 'Nhân viên vận hành', SYSTEM: 'Hệ thống', RESELLER: 'Đại lý', EXTERNAL: 'Bên ngoài' },
  },
  matrix: {
    defaults: 'Quy tắc mặc định',
    defaultsNone: 'Chưa có quy tắc mặc định: TLD không có giá riêng sẽ không báo giá được.',
    rule: { FIXED_PRICE: '{{value}}', MARKUP_FIXED: 'Giá vốn + {{value}}', MARKUP_PERCENT: 'Giá vốn + {{value}} %' },
    ruleMin: 'tối thiểu {{value}}',
    ruleMax: 'tối đa {{value}}',
    op: { REGISTER: 'Đăng ký', RENEW: 'Gia hạn', TRANSFER: 'Chuyển về', RESTORE: 'Khôi phục' },
    oneYear: '1 năm',
    tld: 'TLD',
    book: 'Bảng giá',
    search: 'Tìm TLD',
    onlySpecific: 'Chỉ TLD có giá riêng',
    source: { SPECIFIC: 'riêng', DEFAULT: 'mặc định', FREE: 'miễn phí', NO_COST: 'chưa có giá vốn', NO_RULE: 'chưa có giá', NOT_OFFERED: 'không cung cấp', NO_PRICE: 'không báo giá được' },
    renewalBasis: 'theo giá gia hạn',
    empty: 'Không có TLD nào.',
    note: 'Khách trả cho 1 năm, trước thuế, chưa gồm phí bắt buộc (lệ phí VNNIC, phí ICANN: xem trang TLD).',
    loadError: 'Không tải được giá',
    addSpecific: 'Thêm giá riêng cho TLD…',
    pick: 'Giá riêng cho TLD · {{book}}',
    pickTld: 'TLD',
    pickOp: 'Nghiệp vụ',
    choose: 'Chọn…',
    more: 'Thao tác khác',
    details: 'Lịch sử & chi tiết quy tắc',
    since: 'từ {{date}}',
    cell: {
      title: 'Giá bán .{{zone}} · {{op}} · {{book}}',
      description: 'Phiên bản mới bắt đầu từ thời điểm chọn và đóng phiên bản đang áp dụng; báo giá đã đưa giữ nguyên giá.',
      current: 'Khách đang trả (1 năm, trước thuế)',
      how: 'Cách tính',
      cost: 'Giá vốn registry',
      fromDefault: 'quy tắc mặc định của bảng giá',
      fromSpecific: 'giá riêng của TLD này',
      noCost: 'Nghiệp vụ này chưa có giá vốn: đặt ở trang TLD trước.',
      noRule: 'Không có quy tắc nào áp dụng.',
      notOffered: 'Cấu hình TLD không cho nghiệp vụ này.',
      free: 'Miễn phí theo cấu hình TLD.',
      choice: 'Giá mới',
      useDefault: 'Dùng quy tắc mặc định',
      fixed: 'Giá cố định',
      percent: 'Giá vốn + %',
      markup: 'Giá vốn + số tiền',
      value: 'Giá trị',
      defaultWillApply: 'Sau đó: {{rule}}',
      noDefault: 'Bảng giá không có quy tắc mặc định cho nghiệp vụ này: TLD sẽ không báo giá được.',
      alreadyDefault: 'Đang dùng quy tắc mặc định.',
      from: 'Hiệu lực từ',
      fromHint: 'Để trống = ngay bây giờ.',
      save: 'Lưu giá',
      saved: 'Đã lưu.',
    },
    grid: {
      title: 'Giá bán .{{zone}} · {{book}}',
      description: 'Mọi nghiệp vụ của TLD trong một lần nhập. Nghiệp vụ để "Giữ nguyên" không đổi; các nghiệp vụ đổi được lưu cùng lúc từ thời điểm chọn. Báo giá đã đưa giữ nguyên giá.',
      op: 'Nghiệp vụ',
      current: 'Khách đang trả (1 năm)',
      method: 'Cách tính mới',
      preview: 'Khách sẽ trả',
      keep: 'Giữ nguyên',
      all: 'Áp dụng cho tất cả nghiệp vụ',
      apply: 'Áp dụng',
      costLine: 'giá vốn {{cost}}',
      noCost: 'Chưa có giá vốn',
      noDefault: 'Không có quy tắc mặc định',
      renewal: 'Tính theo giá gia hạn',
      plusRenewal: '+ giá gia hạn',
      save_one: 'Lưu {{count}} nghiệp vụ',
      save_other: 'Lưu {{count}} nghiệp vụ',
      nothing: 'Chưa có nghiệp vụ nào thay đổi.',
      saved_one: 'Đã lưu giá cho {{count}} nghiệp vụ.',
      saved_other: 'Đã lưu giá cho {{count}} nghiệp vụ.',
      pickFirst: 'Chọn TLD để nhập giá cho mọi nghiệp vụ.',
    },
  },
};
